import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  RefreshCw,
  X,
  Filter,
  Ruler,
  ArrowRight,
  Search
} from 'lucide-react';

/**
 * Dedicated filter for raw material (bar stock) inventory.
 * Only two dimensions: diameter (直径) and length (长度).
 * Does not reuse the product-focused AdvancedSearch component.
 */
const RawMaterialFilter = ({
  filters,
  onAdvancedFilterChange,
  onFilterSubmit,
  onClearAllFilters,
  loading = false,
}) => {
  const diameterMin = filters?.tip_diameter_range?.min || '';
  const diameterMax = filters?.tip_diameter_range?.max || '';
  const lengthMin = filters?.total_length_range?.min || '';
  const lengthMax = filters?.total_length_range?.max || '';

  const activeCount = [diameterMin, diameterMax, lengthMin, lengthMax].filter(
    (v) => v !== '' && v != null
  ).length;

  const handleRangeChange = (rangeName, field, value) => {
    onAdvancedFilterChange(`${rangeName}.${field}`, value);
  };

  const handleNumberChange = (rangeName, field, rawValue) => {
    // Allow typing decimals freely; strip any non-numeric / extra-dot input.
    const sanitized = rawValue
      .replace(/[^0-9.]/g, '')
      .replace(/(\..*)\./g, '$1');
    handleRangeChange(rangeName, field, sanitized);
  };

  const handleClear = () => {
    // Only clear the two raw-material dimensions, keep other view state untouched.
    handleRangeChange('tip_diameter_range', 'min', '');
    handleRangeChange('tip_diameter_range', 'max', '');
    handleRangeChange('total_length_range', 'min', '');
    handleRangeChange('total_length_range', 'max', '');
  };

  return (
    <Card className="mb-6">
      <CardContent className="p-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <Ruler className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold">棒料库存筛选</h3>
            {activeCount > 0 && (
              <div className="relative ml-1">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <Badge
                  variant="default"
                  className="absolute -top-2 -right-2 h-4 w-4 p-0 flex items-center justify-center text-xs"
                >
                  {activeCount}
                </Badge>
              </div>
            )}
          </div>
          <div className="flex gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onClearAllFilters}
              disabled={loading}
              title="清除所有筛选"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <form
          className="flex flex-wrap gap-4 items-end"
          onSubmit={(e) => {
            e.preventDefault();
            onFilterSubmit();
          }}
        >
          <RangeField
            icon={<Ruler className="h-4 w-4 text-muted-foreground" />}
            label="直径 (mm)"
            rangeName="tip_diameter_range"
            minValue={diameterMin}
            maxValue={diameterMax}
            onChange={handleNumberChange}
          />

          <RangeField
            icon={<Ruler className="h-4 w-4 text-muted-foreground" />}
            label="长度 (mm)"
            rangeName="total_length_range"
            minValue={lengthMin}
            maxValue={lengthMax}
            onChange={handleNumberChange}
          />

          <div className="flex items-center gap-2">
            <Button type="submit" disabled={loading}>
              <Search className="h-4 w-4 mr-2" />
              搜索
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleClear}
              disabled={loading || activeCount === 0}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              重置
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

const RangeField = ({ icon, label, rangeName, minValue, maxValue, onChange }) => (
  <div className="flex-1 min-w-[220px] space-y-2">
    <Label className="flex items-center gap-2">
      {icon}
      {label}
    </Label>
    <div className="flex items-center gap-2">
      <NumberInput
        value={minValue}
        onChange={(value) => onChange(rangeName, 'min', value)}
        placeholder="最小"
      />
      <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
      <NumberInput
        value={maxValue}
        onChange={(value) => onChange(rangeName, 'max', value)}
        placeholder="最大"
      />
    </div>
  </div>
);

const NumberInput = ({ value, onChange, placeholder }) => (
  <Input
    type="text"
    inputMode="decimal"
    pattern="^[0-9]*\.?[0-9]*$"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
  />
);

export default RawMaterialFilter;
